import { NavLink, useNavigate } from "react-router-dom";
import { Home, FileText, CalendarDays, Receipt, LogOut, BookOpen, Clock } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { EMPLOYEE_NAV } from "@/constants/testIds";
import { AdvisorFab } from "@/components/AdvisorFab";
import { ProductTourFab } from "@/components/ProductTourFab";
import { api } from "@/lib/api";
import { toast } from "sonner";

export function EmployeeLayout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const openUsageGuide = async () => {
    try {
      const { data } = await api.get("/static-guides/employee", { responseType: "blob" });
      window.open(URL.createObjectURL(data), "_blank");
    } catch {
      toast.error("No se pudo abrir la guía de uso.");
    }
  };

  const navItemClass = ({ isActive }) =>
    `flex flex-col items-center gap-1 py-2 px-4 rounded-2xl text-xs font-medium transition-colors ${
      isActive ? "text-terracotta" : "text-charcoal/50"
    }`;

  return (
    <div className="min-h-screen bg-background flex flex-col max-w-md mx-auto">
      <header className="flex items-center justify-between px-5 py-4">
        <div>
          <p className="text-xs text-charcoal/50">Hola,</p>
          <p className="font-heading font-bold text-lg text-charcoal">{user?.name?.split(" ")[0]}</p>
        </div>
        <div className="flex items-center gap-2">
          <button data-testid={EMPLOYEE_NAV.usageGuideButton} onClick={openUsageGuide} className="p-2 rounded-full bg-card border border-petroleum/10" aria-label="Guía de uso">
            <BookOpen className="w-4 h-4 text-petroleum" strokeWidth={1.5} />
          </button>
          <button data-testid={EMPLOYEE_NAV.logoutButton} onClick={handleLogout} className="p-2 rounded-full bg-card border border-petroleum/10" aria-label="Cerrar sesión">
            <LogOut className="w-4 h-4 text-petroleum" strokeWidth={1.5} />
          </button>
        </div>
      </header>
      <main className="flex-1 px-5 pb-28 overflow-y-auto">{children}</main>
      <nav data-testid={EMPLOYEE_NAV.bottomNav} className="fixed bottom-0 left-0 right-0 max-w-md mx-auto bg-card border-t border-petroleum/10 flex justify-around px-2 py-2 shadow-[0_-8px_30px_rgba(14,79,79,0.08)]">
        <NavLink data-testid={EMPLOYEE_NAV.navHome} to="/portal" className={navItemClass} end>
          <Home className="w-5 h-5" strokeWidth={1.5} /> Inicio
        </NavLink>
        <NavLink data-testid={EMPLOYEE_NAV.navExpediente} to="/portal/expediente" className={navItemClass}>
          <FileText className="w-5 h-5" strokeWidth={1.5} /> Expediente
        </NavLink>
        <NavLink data-testid={EMPLOYEE_NAV.navVacaciones} to="/portal/vacaciones" className={navItemClass}>
          <CalendarDays className="w-5 h-5" strokeWidth={1.5} /> Vacaciones
        </NavLink>
        <NavLink data-testid="employee-nav-recibos" to="/portal/recibos" className={navItemClass}>
          <Receipt className="w-5 h-5" strokeWidth={1.5} /> Recibos
        </NavLink>
        <NavLink data-testid="employee-nav-jornada" to="/portal/jornada" className={navItemClass}>
          <Clock className="w-5 h-5" strokeWidth={1.5} /> Jornada
        </NavLink>
      </nav>
      <AdvisorFab role="employee" />
      <ProductTourFab role="employee" />
    </div>
  );
}
